import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Save, Download, FileText, Clock, AlignLeft, Hash, Type } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScriptDrawer = ({ isOpen, onClose, videoId, videoTitle, videoStatus, canalId }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [roteiroId, setRoteiroId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [stats, setStats] = useState({ chars: 0, words: 0, paragraphs: 0 });
  const contentRef = useRef(content);
  const editorRef = useRef(null);

  // Keep ref in sync with content
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  // Stats calculation
  const updateStats = useCallback((text) => {
    const chars = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const paragraphs = text.trim() ? text.trim().split(/\n\n+/).length : 0;
    setStats({ chars, words, paragraphs });
  }, []);

  // Load script on open
  useEffect(() => {
    if (isOpen && videoId) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/roteiros/video/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          if (res.data) {
            setContent(res.data.conteudo || '');
            setRoteiroId(res.data.id);
            updateStats(res.data.conteudo || '');
          } else {
            setContent('');
            setRoteiroId(null);
            updateStats('');
          }
        })
        .catch(() => {
          setContent('');
          setRoteiroId(null);
          updateStats('');
        });
    }
  }, [isOpen, videoId, updateStats]);

  // Save function
  const saveScript = useCallback(async () => {
    if (!videoId || !canalId) {
      console.warn('[ScriptDrawer] Cannot save: missing videoId or canalId', { videoId, canalId });
      if (!canalId) {
        toast.error('Erro: canal não identificado');
      }
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        canal_id: String(canalId),
        video_id: String(videoId),
        conteudo: contentRef.current || ''
      };
      const res = await axios.post(`${API}/roteiros/save-or-create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.id) setRoteiroId(res.data.id);
      setLastSaved(new Date());
      toast.success(t('dashboard.script_saved') || 'Roteiro salvo!');
      // Notify parent to refresh video list (updates checklist_roteiro)
      window.postMessage({ type: 'ROTEIRO_SAVED' }, '*');
    } catch (err) {
      console.error('Error saving script:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Erro desconhecido';
      toast.error(`Erro ao salvar roteiro: ${detail}`);
    } finally {
      setSaving(false);
    }
  }, [videoId, canalId, t]);

  // Auto-save with 3-second debounce after typing stops
  const debounceTimerRef = useRef(null);
  const handleAutoSave = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      saveScript();
    }, 3000);
  }, [saveScript]);

  // Clean up debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // ESC key handler + body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Narration time calculation
  const calcNarrationTime = (wordCount, speed) => {
    const wpm = speed === 'normal' ? 150 : speed === 'slow' ? 120 : 180;
    const totalSeconds = Math.round((wordCount / wpm) * 60);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  // SRT export
  const downloadSRT = () => {
    const lines = content.split('\n').filter(l => l.trim());
    let srt = '';
    let counter = 1;
    lines.forEach((line, i) => {
      const startSec = i * 5;
      const endSec = (i + 1) * 5;
      const start = `00:${String(Math.floor(startSec / 60)).padStart(2, '0')}:${String(startSec % 60).padStart(2, '0')},000`;
      const end = `00:${String(Math.floor(endSec / 60)).padStart(2, '0')}:${String(endSec % 60).padStart(2, '0')},000`;
      srt += `${counter}\n${start} --> ${end}\n${line}\n\n`;
      counter++;
    });
    const blob = new Blob([srt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle || 'roteiro'}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // TXT export
  const downloadTXT = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle || 'roteiro'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle content change
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateStats(newContent);
    handleAutoSave();
  };

  // Get status badge info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'producao':
      case 'em_producao':
        return { label: t('dashboard.production'), color: 'var(--dp-yellow-500, #EAB308)' };
      case 'pronto':
      case 'finalizado':
        return { label: t('dashboard.ready'), color: 'var(--dp-green-500, #34D399)' };
      case 'agendado':
      case 'postados':
        return { label: t('dashboard.scheduled_label'), color: 'var(--dp-purple-500, #A78BFA)' };
      case 'publicado':
        return { label: t('dashboard.published_label'), color: 'var(--dp-blue-500, #0DAEFF)' };
      default:
        return { label: status || '', color: 'var(--dp-text-secondary, #888)' };
    }
  };

  // Time ago helper
  const getTimeSince = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };

  if (!isOpen) return null;

  const statusInfo = getStatusInfo(videoStatus);

  return (
    <>
      <style>{`
        @keyframes dp-drawer-slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes dp-drawer-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .dp-script-drawer-overlay {
          animation: dp-drawer-fade-in 0.2s ease-out forwards;
        }
        .dp-script-drawer-panel {
          animation: dp-drawer-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .dp-script-drawer-panel textarea::-webkit-scrollbar {
          width: 6px;
        }
        .dp-script-drawer-panel textarea::-webkit-scrollbar-track {
          background: transparent;
        }
        .dp-script-drawer-panel textarea::-webkit-scrollbar-thumb {
          background: rgba(13, 174, 255, 0.3);
          border-radius: 3px;
        }
        .dp-script-drawer-panel textarea::-webkit-scrollbar-thumb:hover {
          background: rgba(13, 174, 255, 0.5);
        }
      `}</style>

      {/* Overlay */}
      <div
        className="dp-script-drawer-overlay"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 9998,
        }}
      />

      {/* Drawer Panel */}
      <div
        className="dp-script-drawer-panel"
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '880px',
          maxWidth: '100vw',
          background: 'var(--dp-bg-glass, rgba(17,17,17,0.95))',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          zIndex: 9999,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
        }}>
          {/* Status Badge */}
          {videoStatus && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: 600,
              background: `${statusInfo.color}20`,
              color: statusInfo.color,
              border: `1px solid ${statusInfo.color}40`,
              whiteSpace: 'nowrap',
            }}>
              {statusInfo.label}
            </span>
          )}

          {/* Title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              fontSize: '15px',
              fontWeight: 600,
              color: 'var(--dp-text-primary, #fff)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {videoTitle || t('dashboard.script_title')}
            </h2>
            <span style={{
              fontSize: '11px',
              color: 'var(--dp-text-secondary, #888)',
            }}>
              {t('dashboard.script_title')}
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {/* Auto-save indicator */}
            <span style={{
              fontSize: '10px',
              color: 'var(--dp-green-500, #34D399)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: 'var(--dp-green-500, #34D399)10',
            }}>
              <Clock size={10} />
              {t('dashboard.script_auto_save')}
            </span>

            {/* Save Button */}
            <button
              onClick={saveScript}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
                background: saving ? 'var(--dp-bg-card, #1a1a1a)' : 'var(--dp-blue-500, #0DAEFF)',
                color: saving ? 'var(--dp-text-secondary, #888)' : '#fff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Save size={13} />
              {saving ? t('dashboard.script_saving') : t('dashboard.script_save')}
            </button>

            {/* Download SRT */}
            <button
              onClick={downloadSRT}
              title={t('dashboard.script_download_srt')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
                background: 'var(--dp-bg-card, #1a1a1a)',
                color: 'var(--dp-text-primary, #fff)',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Download size={13} />
              SRT
            </button>

            {/* Download TXT */}
            <button
              onClick={downloadTXT}
              title={t('dashboard.script_download_txt')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
                background: 'var(--dp-bg-card, #1a1a1a)',
                color: 'var(--dp-text-primary, #fff)',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <FileText size={13} />
              TXT
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              title={t('dashboard.script_close')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
                background: 'var(--dp-bg-card, #1a1a1a)',
                color: 'var(--dp-text-secondary, #888)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}>
          {/* Stat Pills */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-bg-card, #1a1a1a)',
            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
            fontSize: '11px',
          }}>
            <Hash size={11} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <span style={{ color: 'var(--dp-text-secondary, #888)' }}>{t('dashboard.script_chars')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{stats.chars.toLocaleString()}</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-bg-card, #1a1a1a)',
            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
            fontSize: '11px',
          }}>
            <Type size={11} style={{ color: 'var(--dp-green-500, #34D399)' }} />
            <span style={{ color: 'var(--dp-text-secondary, #888)' }}>{t('dashboard.script_words')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{stats.words.toLocaleString()}</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-bg-card, #1a1a1a)',
            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
            fontSize: '11px',
          }}>
            <AlignLeft size={11} style={{ color: 'var(--dp-purple-500, #A78BFA)' }} />
            <span style={{ color: 'var(--dp-text-secondary, #888)' }}>{t('dashboard.script_paragraphs')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{stats.paragraphs}</span>
          </div>

          {/* Separator */}
          <div style={{
            width: '1px',
            height: '16px',
            background: 'var(--dp-border-subtle, rgba(255,255,255,0.08))',
            margin: '0 4px',
          }} />

          {/* Narration Speed Pills */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-bg-card, #1a1a1a)',
            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
            fontSize: '11px',
          }}>
            <Clock size={11} style={{ color: 'var(--dp-text-secondary, #888)' }} />
            <span style={{ color: 'var(--dp-text-secondary, #888)' }}>{t('dashboard.script_narration_slow')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{calcNarrationTime(stats.words, 'slow')}</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-blue-500, #0DAEFF)10',
            border: '1px solid var(--dp-blue-500, #0DAEFF)30',
            fontSize: '11px',
          }}>
            <Clock size={11} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <span style={{ color: 'var(--dp-blue-500, #0DAEFF)' }}>{t('dashboard.script_narration_normal')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{calcNarrationTime(stats.words, 'normal')}</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '4px 10px',
            borderRadius: '8px',
            background: 'var(--dp-bg-card, #1a1a1a)',
            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
            fontSize: '11px',
          }}>
            <Clock size={11} style={{ color: 'var(--dp-text-secondary, #888)' }} />
            <span style={{ color: 'var(--dp-text-secondary, #888)' }}>{t('dashboard.script_narration_fast')}:</span>
            <span style={{ color: 'var(--dp-text-primary, #fff)', fontWeight: 600 }}>{calcNarrationTime(stats.words, 'fast')}</span>
          </div>
        </div>

        {/* Editor */}
        <div style={{
          flex: 1,
          padding: '16px 20px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <textarea
            ref={editorRef}
            value={content}
            onChange={handleContentChange}
            placeholder={t('dashboard.script_placeholder')}
            style={{
              flex: 1,
              width: '100%',
              resize: 'none',
              background: 'var(--dp-bg-input, rgba(255,255,255,0.04))',
              border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
              borderRadius: '10px',
              padding: '16px',
              color: 'var(--dp-text-primary, #fff)',
              fontSize: '14px',
              lineHeight: '1.7',
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
              outline: 'none',
              transition: 'border-color 0.2s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--dp-border-subtle, rgba(255,255,255,0.08))';
            }}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px',
          borderTop: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          fontSize: '11px',
          color: 'var(--dp-text-secondary, #888)',
        }}>
          <span>
            {lastSaved
              ? t('dashboard.script_last_saved', { time: getTimeSince(lastSaved) })
              : ''
            }
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {saving && (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'var(--dp-yellow-500, #EAB308)',
                  animation: 'dp-drawer-fade-in 0.5s ease-in-out infinite alternate',
                }} />
                {t('dashboard.script_saving')}
              </>
            )}
            {!saving && lastSaved && (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'var(--dp-green-500, #34D399)',
                }} />
                {t('dashboard.script_saved')}
              </>
            )}
          </span>
        </div>
      </div>
    </>
  );
};

export default ScriptDrawer;
